import RemoveIcon from '@mui/icons-material/Remove'
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
    selectDerivedBasicDetailsState,
    selectDerivedIdentityState,
    selectDerivedProfilePhotoState,
    selectAccreditationDocsForKey,
    selectTeacherAccountState,
    selectTeacherWorkingRights,
    selectTeacherReference,
    selectDerivedEmergencyContactState,
    selectCanRemoveSetting,
    selectTeacherEmploymentSettings,
    selectTeacherLatestService,
    selectRequiresAccreditationLevel,
    selectTeacherWorkingHistory,
} from '../../../../../redux/teacherSlice'
import {
    Accordion,
    AccordionDetails as MUIAccordionDetails,
    AccordionSummary,
    Divider,
    Stack,
    Typography,
    styled,
    Skeleton,
    Box,
    Tooltip,
    IconButton,
} from '@mui/material'
import {
    canEditItem_v2,
    renderAccordionColor_v2,
    renderAccordionText_v2,
    renderAccordionVariant_v2,
} from '../../helper'
import styles from '../../index.module.scss'
import {
    FieldState_v2,
    IdentificationDocument,
    identityDocumentTypeLabels,
    TeacherEmergencyContact,
} from '../../../../../schemas/teacherReg'
import {
    setFromEdit,
    setOpenRegModal,
} from '../../../../../redux/userRegistrationSlice'
import accordionStyles from './index.module.scss'
import { WORKING_RIGHTS_v2 } from '../../../RegistrationModals/components/helper'
import useAccreditationConfig, {
    createKey,
} from '../../../../../hooks/useAccreditationConfig'
import { AusState } from '../../../../../firebase/accreditation-api'
import { REG_STEP, TeacherEmploymentSetting } from '../../../../../redux/types'
import { removeTeacherEmploymentSetting } from '../../../../../firebase/teacherApi'
import { selectUserUid } from '../../../../../redux/userSlice'
import { setErrorMsg } from '../../../../../redux/uiSlice'
import { DEFAULT_ERR_MSG } from '../../../../../helpers/errorMsg'
import { addEmploymentSetting } from '../../../../../firebase/general-apis'
import { logger } from '../../../../../helpers/logger'
import { getError } from '../../../../../helpers/utils'
import { Fragment } from 'react/jsx-runtime'
import { EmploymentSetting } from '../../../../../firebase/types-teacher'
import { ECCAccreditationLevelSelect } from '../../../ReviewPage/helper'
import { DashboardTabNames } from '../../../../../routers/type'
import ESQualificationSelector from '../../../components/ESQualificationSelector'
import dayjs from '../../../../../helpers/dayjs'

interface ITeacherOnboardingAccordions {
    variant?: 'review'
    filterGroup?: (groupState: FieldState_v2) => boolean
}
export const TeacherOnboardingAccordions = (
    args: ITeacherOnboardingAccordions
) => {
    return (
        <Stack gap={3}>
            <BasicDetailsAccordion {...args} />
            <ProfileAccordion {...args} />
            <WorkingRightsAccordion {...args} />
            <IdentityAccordion {...args} />
            <AccreditationAccordionWrapper {...args} />
            <WorkingHistoryAccordion {...args} />
            <ReferenceAccordion {...args} />
            <EmergencyContactsAccordion {...args} />
            <AccredtationLevelAccordion {...args} />
        </Stack>
    )
}

// Child components

interface IIncompleteAccordion extends ITeacherOnboardingAccordions {}

const AccordionDetails = styled(MUIAccordionDetails)(({ theme }) => ({
    '& .MuiTypography-root': {
        ...theme.typography.pm,
        color: theme.palette.shades100.main,
    },
}))

const FieldEditButton = ({
    itemState,
    step,
    hide,
}: {
    itemState: FieldState_v2
    step: string
    hide?: boolean
}) => {
    const accountState = useSelector(selectTeacherAccountState)
    const dispatch = useDispatch()
    const handleEdit = () => {
        batch(() => {
            dispatch(setFromEdit(step))
            dispatch(setOpenRegModal(true))
        })
    }

    if (hide || accountState.status !== 'success') return null

    if (canEditItem_v2(accountState.data, itemState)) {
        return (
            <Typography
                style={{
                    color: renderAccordionColor_v2(itemState),
                    cursor: 'pointer',
                }}
                onClick={handleEdit}
            >
                Edit
            </Typography>
        )
    }
}

export const positiveStates = new Set<FieldState_v2>(['approved', 'submitted'])

export const BasicDetailsAccordion = (props: IIncompleteAccordion) => {
    const basicDetails = useSelector(selectDerivedBasicDetailsState)
    const accountState = useSelector(selectTeacherAccountState)

    if (basicDetails.loading || accountState.status !== 'success') {
        return <Skeleton />
    }

    const { values, state, itemStates } = basicDetails

    return (
        <Accordion
            variant={renderAccordionVariant_v2(
                state,
                props.variant === 'review'
            )}
            expanded={props.variant === 'review' ? true : undefined}
        >
            <AccordionSummary
                expandIcon={
                    // prettier-ignore
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </svg>
                }
            >
                <Stack>
                    <Typography variant="pl">Basic Details</Typography>
                    <Typography variant="pl">
                        {renderAccordionText_v2(accountState.data, state)}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                className={styles.accordionDetails}
                sx={{ backgroundColor: styles.shades0 }}
            >
                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(itemStates.names)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Names
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={itemStates.names}
                        step={'names'}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                <Divider flexItem />
                <Stack className={styles.contentData}>
                    <Typography>First Name</Typography>
                    <Typography>{values.firstName}</Typography>
                </Stack>
                <Divider flexItem />
                <Stack className={styles.contentData}>
                    <Typography>Last Name</Typography>
                    <Typography>{values.lastName}</Typography>
                </Stack>
                <Divider flexItem />
                <Stack className={styles.contentData}>
                    <Typography>Actual First Name</Typography>
                    <Typography>{values.firstNameActual || '-'}</Typography>
                </Stack>
                <Divider flexItem />

                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(itemStates.contact)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Contact Information
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={itemStates.contact}
                        step={'contact'}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                <Divider flexItem />
                <Stack className={styles.contentData}>
                    <Typography>Email</Typography>
                    <Typography>{values.email}</Typography>
                </Stack>
                <Divider flexItem />
                <Stack className={styles.contentData}>
                    <Typography>Contact Number</Typography>
                    <Typography>{values.contact}</Typography>
                </Stack>
                <Divider flexItem />
                <Stack className={styles.contentData}>
                    <Typography>Address</Typography>
                    <Typography>{values.homeLocation}</Typography>
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}

export const ProfileAccordion = (props: IIncompleteAccordion) => {
    const profilePicture = useSelector(selectDerivedProfilePhotoState)
    const accountState = useSelector(selectTeacherAccountState)

    if (profilePicture.loading || accountState.status !== 'success')
        return <Skeleton />

    return (
        <Accordion
            variant={renderAccordionVariant_v2(
                profilePicture.state,
                props.variant === 'review'
            )}
            expanded={props.variant === 'review' ? true : undefined}
        >
            <AccordionSummary
                expandIcon={
                    // prettier-ignore
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </svg>
                }
            >
                <Stack>
                    <Typography variant="pl">Profile Photo</Typography>
                    <Typography variant="pl">
                        {renderAccordionText_v2(
                            accountState.data,
                            profilePicture.state
                        )}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                className={styles.accordionDetails}
                sx={{ backgroundColor: styles.shades0 }}
            >
                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(profilePicture.state)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Profile Photo
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={profilePicture.state}
                        step={REG_STEP.PROFILE_PHOTO}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                {typeof profilePicture.values.value === 'string' ? (
                    <>
                        <Divider flexItem />
                        <Box className={accordionStyles.imgContainer} ml="auto">
                            <img
                                src={profilePicture.values.value}
                                alt={'Uploaded user profile'}
                            />
                        </Box>
                    </>
                ) : null}
            </AccordionDetails>
        </Accordion>
    )
}

export const IdentityAccordion = (props: IIncompleteAccordion) => {
    const identityDocs = useSelector(selectDerivedIdentityState)

    const section = (doc: IdentificationDocument) => (
        <>
            <Divider flexItem />
            <Stack className={styles.contentData}>
                <Typography>ID Type</Typography>
                <Typography>
                    {doc.documentType
                        ? identityDocumentTypeLabels[doc.documentType]
                        : '-'}
                </Typography>
            </Stack>
            <Divider flexItem />
            <Stack className={styles.contentData}>
                <Typography>Country of Issue</Typography>
                <Typography>{doc.country || '-'}</Typography>
            </Stack>
            <Divider flexItem />
            <Stack className={styles.contentData}>
                <Typography>Name on Document</Typography>
                <Typography>{doc.name || '-'}</Typography>
            </Stack>
            <Divider flexItem />
            <Stack className={styles.contentData}>
                <Typography>Date of Birth</Typography>
                <Typography>{doc.birthDate || '-'}</Typography>
            </Stack>
            <Divider flexItem />
            <Stack className={styles.contentData}>
                <Typography>Document Number</Typography>
                <Typography>{doc.reference || '-'}</Typography>
            </Stack>
            <Divider flexItem />
            <Stack className={styles.contentData}>
                <Typography>Document Uploaded YES/NO</Typography>
                <Typography>{doc.value ? 'YES' : 'NO'}</Typography>
            </Stack>
            <Divider flexItem />
        </>
    )
    const accountState = useSelector(selectTeacherAccountState)

    if (identityDocs.loading || accountState.status !== 'success')
        return <Skeleton />

    const { state, itemStates, values } = identityDocs

    return (
        <Accordion
            variant={renderAccordionVariant_v2(
                state,
                props.variant === 'review'
            )}
            expanded={props.variant === 'review' ? true : undefined}
        >
            <AccordionSummary
                expandIcon={
                    // prettier-ignore
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </svg>
                }
            >
                <Stack>
                    <Typography variant="pl">Identity Verfication</Typography>
                    <Typography variant="pl">
                        {renderAccordionText_v2(accountState.data, state)}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                className={styles.accordionDetails}
                sx={{ backgroundColor: styles.shades0 }}
            >
                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(itemStates.primary)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Primary Identification
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={itemStates.primary}
                        step={'primaryIdentity'}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                <Divider flexItem />
                {section(values.primary.data)}
                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(itemStates.secondary)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Secondary Identification
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={itemStates.secondary}
                        step={'secondaryIdentity'}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                <Divider flexItem />
                {section(values.secondary.data)}

                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(itemStates.photoHoldingId)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Photo Holding ID
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={itemStates.photoHoldingId}
                        step={'photoHoldingID'}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                <Divider flexItem />
                <Stack className={styles.contentData}>
                    <Typography>Document Uploaded YES/NO</Typography>
                    <Typography>
                        {values.photoHoldingId.data.value ? 'YES' : 'NO'}
                    </Typography>
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}

export const WorkingRightsAccordion = (props: IIncompleteAccordion) => {
    const workingRights = useSelector(selectTeacherWorkingRights)
    const accountState = useSelector(selectTeacherAccountState)

    if (workingRights.status !== 'success' || accountState.status !== 'success')
        return <Skeleton />

    const { data } = workingRights

    return (
        <Accordion
            variant={renderAccordionVariant_v2(
                data.state,
                props.variant === 'review'
            )}
            expanded={props.variant === 'review' ? true : undefined}
        >
            <AccordionSummary
                expandIcon={
                    // prettier-ignore
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </svg>
                }
            >
                <Stack>
                    <Typography variant="pl">Working Rights</Typography>
                    <Typography variant="pl">
                        {renderAccordionText_v2(
                            accountState.data,
                            workingRights.data.state
                        )}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                className={styles.accordionDetails}
                sx={{ backgroundColor: styles.shades0 }}
            >
                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(data.state)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Document
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={data.state}
                        step={'workingRights'}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                <Divider flexItem />
                <Stack className={styles.contentData}>
                    <Typography>Working Rights</Typography>
                    <Typography>
                        {data.value === null
                            ? '-'
                            : WORKING_RIGHTS_v2[data.value]}
                    </Typography>
                </Stack>
                {data.value === 'temporaryVisaHolder' ? (
                    <>
                        <Divider flexItem />
                        <Stack className={styles.contentData}>
                            <Typography>Document Uploaded? YES/NO</Typography>
                            <Typography>
                                {data.url === null ? 'NO' : 'YES'}
                            </Typography>
                        </Stack>
                    </>
                ) : null}
            </AccordionDetails>
        </Accordion>
    )
}

export const AccreditationAccordionWrapper = (args: IIncompleteAccordion) => {
    const settings = useSelector(selectTeacherEmploymentSettings)

    if (settings.status !== 'success') return null

    return Object.entries(settings.data).map(([key, el], i) => (
        <AccreditationAccordion
            {...args}
            setting={el}
            settingId={key}
            key={`AccreditationAccordion-${i}`}
        />
    ))
}

const settingLabelMap: Record<string, string> = {
    school: 'Teaching',
    earlyChildcare: 'Early Childhood',
    supportStaff: 'Education Support Staff',
}

export const AccreditationAccordion = ({
    setting,
    settingId,
    variant,
}: {
    setting: EmploymentSetting
    settingId: string
} & IIncompleteAccordion) => {
    const canRemove = useSelector(selectCanRemoveSetting)
    const docs = useSelector(selectAccreditationDocsForKey(createKey(setting)))
    const accountState = useSelector(selectTeacherAccountState)

    // const settingId = useSelector((state: RootState) =>
    //     selectEmploymentSettingId(state, setting.locationState, setting.type)
    // )

    const { data, isPending } = useAccreditationConfig(setting)

    const userId = useSelector(selectUserUid)

    const dispatch = useDispatch()
    const handleRemoveSetting = async () => {
        try {
            if (!userId || !settingId) throw new Error('Invalid arguments')
            await removeTeacherEmploymentSetting(userId, settingId)
        } catch (e) {
            dispatch(setErrorMsg(DEFAULT_ERR_MSG))
        }
    }

    if (docs.loading || isPending || accountState.status !== 'success')
        return <Skeleton />

    return (
        <Accordion
            variant={renderAccordionVariant_v2(
                docs.state,
                variant === 'review'
            )}
            expanded={variant === 'review' ? true : undefined}
        >
            <AccordionSummary
                expandIcon={
                    // prettier-ignore
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </svg>
                }
            >
                <Stack>
                    <Stack direction={'row'} flex={1} alignItems={'center'}>
                        {canRemove ? (
                            <Tooltip title="Remove" placement="top" arrow>
                                <IconButton
                                    sx={{ mr: 1 }}
                                    className="accordion-iconBtn"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        return handleRemoveSetting()
                                    }}
                                >
                                    <RemoveIcon />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        <Typography variant="pl">
                            {settingLabelMap[setting.type] || ''} Accreditation
                        </Typography>
                    </Stack>
                    <Typography variant="pl">
                        {renderAccordionText_v2(accountState.data, docs.state)}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                className={styles.accordionDetails}
                sx={{ backgroundColor: styles.shades0 }}
            >
                {/* Setting specific settings */}
                {setting.type === 'earlyChildcare' ? (
                    <Stack className={styles.contentData}>
                        <Typography>
                            My Early Childhood Accreditation Level is:
                        </Typography>
                        <ECCAccreditationLevelSelect
                            settingId={settingId}
                            level={setting.level}
                            locationState={setting.locationState}
                            disabled={
                                location.pathname.includes(
                                    DashboardTabNames.REVIEW
                                ) ||
                                (accountState.data === 'submitted' &&
                                    setting.state !== 'rejected')
                            }
                        />
                    </Stack>
                ) : setting.type === 'supportStaff' ? (
                    <Stack className={styles.contentData}>
                        <Typography variant="ps">
                            Have you completed any relevant tertiary or other
                            formal qualifications in education? (e.g.,
                            certificate, diploma, university degree.)*
                        </Typography>
                        <ESQualificationSelector
                            employmentSetting={setting}
                            settingId={settingId}
                        />
                    </Stack>
                ) : null}
                {/* TODO: Sort by  config?.short_title */}
                {docs.docs
                    .sort((a, b) => {
                        const configA = data?.[a.type]
                        const configB = data?.[b.type]

                        if (configA?.optional === configB?.optional) {
                            return (configA?.short_title || '').localeCompare(
                                configB?.short_title || ''
                            )
                        } else if (configA?.optional) {
                            return 1
                        } else if (configB?.optional) {
                            return -1
                        }
                        return 0
                    })
                    .map((d, i) => {
                        const config = data?.[d.type]
                        return (
                            <Fragment
                                key={`accreditation_doc_${setting.locationState}:${setting.type}_${d.type}_${i}`}
                            >
                                <Stack className={styles.contentData}>
                                    <Typography
                                        className={
                                            positiveStates.has(d.doc.state)
                                                ? 'accordion-positive'
                                                : 'accordion-subtitle'
                                        }
                                    >
                                        {config?.optional ? '(Optional) ' : ''}
                                        {config?.short_title}
                                    </Typography>
                                    {/* Edit Button */}
                                    <FieldEditButton
                                        itemState={d.doc.state}
                                        step={d.type}
                                        hide={variant === 'review'}
                                    />
                                </Stack>
                                <Divider flexItem />
                                {!config?.required_datefield ? null : config?.required_datefield ===
                                  'registrationDate' ? (
                                    <>
                                        <Stack className={styles.contentData}>
                                            <Typography>
                                                Date Completed
                                            </Typography>
                                            <Typography>
                                                {d.doc.registrationDate || '-'}
                                            </Typography>
                                        </Stack>
                                        <Divider flexItem />
                                    </>
                                ) : (
                                    <>
                                        <Stack className={styles.contentData}>
                                            <Typography>Expiry Date</Typography>
                                            <Typography>
                                                {d.doc.expiryDate || '-'}
                                            </Typography>
                                        </Stack>
                                        <Divider flexItem />
                                    </>
                                )}
                                {config?.reference_required ? null : (
                                    <>
                                        <Stack className={styles.contentData}>
                                            <Typography>
                                                Document Reference
                                            </Typography>
                                            <Typography>
                                                {d.doc.reference || '-'}
                                            </Typography>
                                        </Stack>
                                        <Divider flexItem />
                                    </>
                                )}

                                <Stack className={styles.contentData}>
                                    <Typography>
                                        Document Uploaded YES/NO
                                    </Typography>
                                    <Typography>
                                        {d.doc.imageURL !== null ? 'YES' : 'NO'}
                                    </Typography>
                                </Stack>
                                <Divider flexItem />
                            </Fragment>
                        )
                    })}

                {setting.type === 'school' ||
                setting.type === 'earlyChildcare' ? (
                    <AddSetting
                        type={setting.type}
                        locationState={setting.locationState}
                        hide={variant === 'review'}
                    />
                ) : null}
            </AccordionDetails>
        </Accordion>
    )
}

const AddSetting = ({
    type,
    locationState,
    hide,
}: {
    type: Exclude<TeacherEmploymentSetting, 'supportStaff'>
    locationState: AusState
    hide?: boolean
}) => {
    const settings = useSelector(selectTeacherEmploymentSettings)
    const userUid = useSelector(selectUserUid)
    const dispatch = useDispatch()

    const handleAddSetting = () => {
        try {
            if (!userUid) throw new Error('Invalid user')
            return addEmploymentSetting(
                userUid,
                locationState,
                type === 'school' ? 'earlyChildcare' : 'school'
            )
        } catch (e) {
            logger.debug(getError(e))
            dispatch(setErrorMsg(DEFAULT_ERR_MSG))
        }
    }

    if (settings.status !== 'success') return null

    const show = Object.entries(settings.data).length === 1 && !hide

    if (!show) return null
    const label = type === 'school' ? 'Educator' : 'Teacher'
    return (
        <Stack
            role="button"
            sx={{ cursor: 'pointer' }}
            onClick={handleAddSetting}
            flex={1}
            direction={'row'}
            justifyContent={'center'}
            alignItems={'center'}
            gap={1.5}
            pt={1.5}
        >
            <Divider flexItem sx={{ width: '100%' }}>
                <Typography>Add {label} Application</Typography>
            </Divider>
        </Stack>
    )
}

export const ReferenceAccordion = (props: IIncompleteAccordion) => {
    const reference = useSelector(selectTeacherReference)
    const accountState = useSelector(selectTeacherAccountState)

    if (reference.status !== 'success' || accountState.status !== 'success')
        return <Skeleton />

    return (
        <Accordion
            variant={renderAccordionVariant_v2(
                reference.data.state,
                props.variant === 'review'
            )}
            expanded={props.variant === 'review' ? true : undefined}
        >
            <AccordionSummary
                expandIcon={
                    // prettier-ignore
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </svg>
                }
            >
                <Stack>
                    <Typography variant="pl">Reference</Typography>
                    <Typography variant="pl">
                        {renderAccordionText_v2(
                            accountState.data,
                            reference.data.state
                        )}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                className={styles.accordionDetails}
                sx={{ backgroundColor: styles.shades0 }}
            >
                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(reference.data.state)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Reference
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={reference.data.state}
                        step={'reference'}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                <Divider flexItem />

                {!!reference.data.requestedInterview ? (
                    <Stack className={styles.contentData}>
                        <Typography>Requested for Online Interview</Typography>
                    </Stack>
                ) : (
                    <>
                        <Stack className={styles.contentData}>
                            <Typography>First Name</Typography>
                            <Typography>
                                {reference.data.firstName || '-'}
                            </Typography>
                        </Stack>
                        <Stack className={styles.contentData}>
                            <Typography>Last Name</Typography>
                            <Typography>
                                {reference.data.lastName || '-'}
                            </Typography>
                        </Stack>
                        <Stack className={styles.contentData}>
                            <Typography>Email</Typography>
                            <Typography>
                                {reference.data.email || '-'}
                            </Typography>
                        </Stack>
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    )
}

export const EmergencyContactsAccordion = (props: IIncompleteAccordion) => {
    const data = useSelector(selectDerivedEmergencyContactState)
    const accountState = useSelector(selectTeacherAccountState)

    if (data.loading || accountState.status !== 'success') return <Skeleton />

    const section = (contact: TeacherEmergencyContact) => {
        return (
            <>
                <Stack className={styles.contentData}>
                    <Typography>Name</Typography>
                    <Typography>{contact.name}</Typography>
                </Stack>
                <Divider flexItem />

                <Stack className={styles.contentData}>
                    <Typography>Relationship</Typography>
                    <Typography>{contact.relationship}</Typography>
                </Stack>
                <Divider flexItem />

                <Stack className={styles.contentData}>
                    <Typography>Contact</Typography>
                    <Typography>{contact.contact}</Typography>
                </Stack>
                <Divider flexItem />
            </>
        )
    }

    return (
        <Accordion
            variant={renderAccordionVariant_v2(
                data.state,
                props.variant === 'review'
            )}
            expanded={props.variant === 'review' ? true : undefined}
        >
            <AccordionSummary
                expandIcon={
                    // prettier-ignore
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </svg>
                }
            >
                <Stack>
                    <Typography variant="pl">Emergency Contacts</Typography>
                    <Typography variant="pl">
                        {renderAccordionText_v2(accountState.data, data.state)}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                className={styles.accordionDetails}
                sx={{ backgroundColor: styles.shades0 }}
            >
                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(data.itemStates.primary)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Primary Contact
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={data.itemStates.primary}
                        step={REG_STEP.EMERGENCY_CONTACTS}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                <Divider flexItem />
                {section(data.values.primary)}

                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(data.itemStates.secondary)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Secondary Contact
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={data.itemStates.secondary}
                        step={REG_STEP.EMERGENCY_CONTACTS}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                <Divider flexItem />
                {section(data.values.secondary)}
            </AccordionDetails>
        </Accordion>
    )
}

export const AccredtationLevelAccordion = (props: IIncompleteAccordion) => {
    const required = useSelector(selectRequiresAccreditationLevel)
    const accountState = useSelector(selectTeacherAccountState)
    const service = useSelector(selectTeacherLatestService, shallowEqual)

    if (!required) return null

    if (accountState.status !== 'success' || service === undefined)
        return <Skeleton />

    const state = service !== null ? 'submitted' : 'incomplete'

    return (
        <Accordion
            variant={renderAccordionVariant_v2(
                state,
                props.variant === 'review'
            )}
            expanded={props.variant === 'review' ? true : undefined}
        >
            <AccordionSummary
                expandIcon={
                    // prettier-ignore
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </svg>
                }
            >
                <Stack>
                    <Typography variant="pl">Accreditation Level</Typography>
                    <Typography variant="pl">
                        {renderAccordionText_v2(accountState.data, state)}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                className={styles.accordionDetails}
                sx={{ backgroundColor: styles.shades0 }}
            >
                <Stack className={styles.contentData}>
                    <Typography
                        className={
                            positiveStates.has(state)
                                ? 'accordion-positive'
                                : 'accordion-subtitle'
                        }
                    >
                        Casual Teacher Band
                    </Typography>
                    {/* Edit Button */}
                    <FieldEditButton
                        itemState={state}
                        step={REG_STEP.ACCREDITATION_LEVEL}
                        hide={props.variant === 'review'}
                    />
                </Stack>
                <Divider flexItem />

                <Stack className={styles.contentData}>
                    <Typography>
                        Statement of Service Uploaded (YES/NO)
                    </Typography>
                    <Typography>
                        {state === 'submitted' ? 'YES' : 'NO'}
                    </Typography>
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}

export const WorkingHistoryAccordion = (props: IIncompleteAccordion) => {
    const data = useSelector(selectTeacherWorkingHistory)
    const accountState = useSelector(selectTeacherAccountState)

    if (data.status !== 'success' || accountState.status !== 'success')
        return <Skeleton />

    return (
        <Accordion
            variant={renderAccordionVariant_v2(
                data.data.state,
                props.variant === 'review'
            )}
            expanded={props.variant === 'review' ? true : undefined}
        >
            <AccordionSummary
                expandIcon={
                    // prettier-ignore
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </svg>
                }
            >
                <Stack>
                    <Typography variant="pl">
                        Working History with Children
                    </Typography>
                    <Typography variant="pl">
                        {renderAccordionText_v2(
                            accountState.data,
                            data.data.state
                        )}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                className={styles.accordionDetails}
                sx={{ backgroundColor: styles.shades0 }}
            >
                {data.data.entryCount === 0 &&
                data.data.state === 'submitted' ? (
                    <Stack
                        sx={{
                            p: 2,
                        }}
                    >
                        <Typography variant="ps">No experience</Typography>
                        <Typography
                            variant="ps"
                            sx={{
                                color: `${styles.neutrals500} !important`,
                            }}
                        >
                            I have not worked with children.
                        </Typography>
                    </Stack>
                ) : null}

                {Object.entries(data.data.entries || {}).map(
                    ([key, data], i) => {
                        const start = dayjs(data.startDate, 'MM-YYYY')
                        const end = data.endDate
                            ? dayjs(data.endDate, 'MM-YYYY')
                            : null

                        return (
                            <Fragment key={`working-history-accordion-${i}`}>
                                <Stack
                                    sx={{
                                        p: 2,
                                    }}
                                >
                                    <Typography variant="ps">
                                        {data.employer}
                                    </Typography>
                                    <Typography
                                        variant="ps"
                                        sx={{
                                            color: `${styles.neutrals500} !important`,
                                        }}
                                    >
                                        {data.employmentType} (
                                        {start.format('MMM YY')} -{' '}
                                        {end === null
                                            ? 'Present'
                                            : end.format('MMM YY')}{' '}
                                        •{' '}
                                        {dayjs
                                            .duration(
                                                (end === null
                                                    ? dayjs()
                                                    : end
                                                ).diff(start)
                                            )
                                            .humanize()}
                                        )
                                    </Typography>
                                </Stack>
                                <Divider flexItem />
                            </Fragment>
                        )
                    }
                )}
                {props.variant !== 'review' ? (
                    <Divider flexItem sx={{ my: 3 }}>
                        <FieldEditButton
                            itemState={data.data.state}
                            step={REG_STEP.WORKING_HISTORY}
                        />
                    </Divider>
                ) : null}
            </AccordionDetails>
        </Accordion>
    )
}
